import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { AppHeader } from '../components/AppHeader';
import { ProviderLogo } from '../components/ProviderLogo';
import { OpportunityReviewTable } from '../components/OpportunityReviewTable';
import { ResearchDrawer } from '../components/ResearchDrawer';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { disconnectNudgeMailbox, draftNextFollowUp, getResearchRun, listNudgeOpportunities, researchAndDraft, scanNudgeMailbox, setNudgeStatus, syncWatchedThreads, updateNudgeOpportunity } from '../services/appStateService';
import { useEffect, useMemo, useRef, useState } from 'react';
import { connectMailbox } from '../services/mailboxService';
import { UpgradeModal } from '../components/UpgradeModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

const MAILBOX_URLS = {
  google: { inbox: 'https://mail.google.com/mail/u/0/#inbox', drafts: 'https://mail.google.com/mail/u/0/#drafts' },
  microsoft: { inbox: 'https://outlook.office.com/mail/inbox', drafts: 'https://outlook.office.com/mail/drafts' },
  imap: { inbox: '', drafts: '' },
  yahoo: { inbox: 'https://mail.yahoo.com/', drafts: 'https://mail.yahoo.com/', },
  icloud: { inbox: 'https://www.icloud.com/mail/', drafts: 'https://www.icloud.com/mail/', },
};

const SUPPORTED_PROVIDERS = ['google', 'microsoft', 'imap', 'yahoo', 'icloud'];

async function callNudge(name, data = {}) {
  if (!functions) throw new Error('Firebase Functions is not configured.');
  const response = await httpsCallable(functions, name)(data);
  return response.data || {};
}


function providerDisplayName(provider = '') {
  if (provider === 'google') return 'Gmail';
  if (provider === 'microsoft') return 'Outlook';
  if (provider === 'yahoo') return 'Yahoo';
  if (provider === 'icloud') return 'iCloud';
  if (provider === 'imap') return 'IMAP';
  return 'Mailbox';
}

function mailboxNeedsReconnect(mailbox = {}) {
  const text = `${mailbox.health || ''} ${mailbox.lastScanStatus || ''} ${mailbox.lastScanError || ''}`.toLowerCase();
  return (
    mailbox.health === 'limited_permissions' ||
    mailbox.lastScanStatus === 'permission_error' ||
    mailbox.health === 'unhealthy' ||
    text.includes('permission') ||
    text.includes('token') ||
    text.includes('expired') ||
    text.includes('auth')
  );
}

function mailboxStatusCopy(mailbox = {}, active = false) {
  if (mailboxNeedsReconnect(mailbox)) return 'Needs reconnect';
  if (mailbox.health === 'disconnected' || mailbox.status === 'disconnected') return 'Disconnected';
  return `${active ? 'Active' : 'Paused'} · ${mailbox.mode === 'send' ? 'Auto-send' : 'Draft mode'}`;
}

function formatWhen(value) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value || 0));
}

function displayPlanLabel(subscription = {}) {
  const raw = String(subscription?.limits?.label || subscription?.planId || '').trim();
  const normalized = raw.toLowerCase().replace(/-/g, '_');
  const map = {
    early_access: 'Trial',
    earlyaccess: 'Trial',
    free: 'Free',
    trial: 'Trial',
    legacy_pro: 'Legacy Pro',
    pro: 'Legacy Pro',
    pro_ph_trial: 'Product Hunt Pro',
    pro_2026: 'Pro',
    agency_6: 'Agency',
    agency_15: 'Agency',
    agency_25: 'Agency',
    agency_50: 'Agency',
    appsumo_tier_1: 'AppSumo Tier 1',
    appsumo_tier_2: 'AppSumo Tier 2',
    appsumo_tier_3: 'AppSumo Tier 3',
    appsumo_tier_4: 'AppSumo Tier 4',
    appsumo_tier_5: 'AppSumo Tier 5',
  };
  if (map[normalized]) return map[normalized];
  if (!raw) return 'Trial';
  return raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function Metric({ icon, label, value, note }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(141,149,255,.12)', color: 'secondary.main' }}>{icon}</Box>
          <Box>
            <Typography variant="h3" sx={{ fontSize: 28 }}>{value}</Typography>
            <Typography color="text.secondary" variant="body2">{label}</Typography>
          </Box>
        </Stack>
        {note && <Typography variant="caption" color="text.secondary" sx={{ mt: 1.3, display: 'block' }}>{note}</Typography>}
      </CardContent>
    </Card>
  );
}

function ExternalButton({ href, children, variant = 'outlined', startIcon, sx = {} }) {
  return (
    <Button
      component="a"
      href={href}
      target="_blank"
      rel="noreferrer"
      variant={variant}
      startIcon={startIcon}
      sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100%', sm: 150 }, px: 2.4, ...sx }}
    >
      {children}
    </Button>
  );
}

function ActivityIcon({ type }) {
  if (type === 'reply_received' || type === 'recipient_replied_before_send' || type === 'recipient_replied_before_draft') return <MarkEmailReadOutlinedIcon fontSize="small" />;
  if (type === 'followup_sent') return <SendRoundedIcon fontSize="small" />;
  if (type === 'draft_deleted') return <WarningAmberRoundedIcon fontSize="small" />;
  if (type === 'followup_drafted') return <DraftsOutlinedIcon fontSize="small" />;
  return <TaskAltRoundedIcon fontSize="small" />;
}

function ActivityRow({ activity }) {
  const when = formatWhen(activity.createdAt);
  return (
    <Stack direction="row" spacing={1.4} alignItems="flex-start" sx={{ py: 1.2, borderBottom: '1px solid', borderColor: 'divider', '&:last-of-type': { borderBottom: 0 } }}>
      <Box sx={{ mt: .2, width: 34, height: 34, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: activity.severity === 'warning' ? 'rgba(255,179,71,.16)' : 'rgba(74,194,143,.14)', color: activity.severity === 'warning' ? 'primary.dark' : 'success.dark', flex: '0 0 auto' }}>
        <ActivityIcon type={activity.type} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography fontWeight={800}>{activity.subject || 'Gmail thread'}</Typography>
          {when && <Typography variant="caption" color="text.secondary">{when}</Typography>}
        </Stack>
        <Typography color="text.secondary" variant="body2">{activity.message || 'Nudge updated this conversation.'}</Typography>
      </Box>
    </Stack>
  );
}

export function HomePage() {
  const { state, update, refreshAppState } = useApp();
  const [statusError, setStatusError] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [draftError, setDraftError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState('');
  const [activationScanResult, setActivationScanResult] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  const [opportunityError, setOpportunityError] = useState('');
  const [opportunityActionId, setOpportunityActionId] = useState('');
  const [mailboxActionId, setMailboxActionId] = useState('');
  const [researchActionId, setResearchActionId] = useState('');
  const [researchDrawerOpen, setResearchDrawerOpen] = useState(false);
  const [researchDrawerItem, setResearchDrawerItem] = useState(null);
  const [researchDrawerData, setResearchDrawerData] = useState(null);
  const [researchDrawerLoading, setResearchDrawerLoading] = useState(false);
  const [researchDrawerError, setResearchDrawerError] = useState('');
  const [addConversationOpen, setAddConversationOpen] = useState(false);
  const [sentConversations, setSentConversations] = useState([]);
  const [sentConversationsLoading, setSentConversationsLoading] = useState(false);
  const [sentConversationsError, setSentConversationsError] = useState('');
  const [sentConversationSearch, setSentConversationSearch] = useState('');
  const [addingConversationId, setAddingConversationId] = useState('');
  const navigate = useNavigate();
  const backgroundRefreshInFlightRef = useRef(false);
  const lastProviderSyncAtRef = useRef(0);
  const a = state.analytics;
  const activeWorkspaceId = state.activeWorkspaceId || state.defaultWorkspaceId || '';
  const canManageOrganization = Boolean(state.access?.canManageOrganization);
  const activeWorkspace = (state.workspaces || []).find((workspace) => workspace.id === activeWorkspaceId) || null;
  const allConnectedMailboxes = useMemo(() => state.mailboxes.filter((mailbox) => SUPPORTED_PROVIDERS.includes(mailbox.provider) && mailbox.status !== 'disconnected' && mailbox.health !== 'disconnected' && mailbox.connected !== false), [state.mailboxes]);
  const connectedMailboxes = useMemo(() => allConnectedMailboxes.filter((mailbox) => !activeWorkspaceId || mailbox.workspaceId === activeWorkspaceId), [allConnectedMailboxes, activeWorkspaceId]);
  const activeConnectedMailboxes = useMemo(() => connectedMailboxes.filter((mailbox) => mailbox.active !== false), [connectedMailboxes]);
  const primaryMailbox = activeConnectedMailboxes[0] || connectedMailboxes[0] || null;
  const visibleSentConversations = useMemo(() => {
    const q = sentConversationSearch.trim().toLowerCase();
    if (!q) return sentConversations;
    return sentConversations.filter((item) =>
      [item.subject, ...(item.recipientEmails || [])].join(' ').toLowerCase().includes(q)
    );
  }, [sentConversations, sentConversationSearch]);
  const hasSupportedMailbox = Boolean(primaryMailbox);
  const hasActiveConnectedMailbox = activeConnectedMailboxes.length > 0;
  const providerName = providerDisplayName(primaryMailbox?.provider);
  const providerNotice = hasSupportedMailbox ? `${providerName} is connected.` : 'Connect Gmail, Outlook, Yahoo, iCloud, or IMAP to start.';
  const mailboxUrls = MAILBOX_URLS[primaryMailbox?.provider] || {};
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const planId = String(state.subscription?.planId || '').toLowerCase().replace(/-/g, '_');
  const isPaidPlan = Boolean(planId) && !['free', 'early_access'].includes(planId);

  const creditPercent = Math.min(100, Math.round((a.creditsUsed / Math.max(1, a.creditsTotal)) * 100));
  const creditsRemaining = Math.max(0, Number(a.creditsTotal || 0) - Number(a.creditsUsed || 0));
  const creditsExhausted = creditsRemaining <= 0;
  const nextFollowUp = formatWhen(a.nextFollowUpAt);
  const showNextFollowUp = Boolean(state.active && hasActiveConnectedMailbox && nextFollowUp);
  const setupSummary = hasActiveConnectedMailbox
    ? `Watching ${formatNumber(a.watching)} active conversation${Number(a.watching || 0) === 1 ? '' : 's'}: ${a.pendingDrafts || 0} draft${a.pendingDrafts === 1 ? '' : 's'} waiting for review, ${a.awaitingReply || 0} sent follow-up${a.awaitingReply === 1 ? '' : 's'} waiting for reply.`
    : `No active mailbox is connected to ${activeWorkspace?.name || 'this workspace'}. Connect or move a mailbox here to scan and draft in this workspace.`;
  const planLimits = state.subscription?.limits || {};
  const planName = displayPlanLabel(state.subscription);
  const mailboxLimit = Math.max(1, Number(planLimits.mailboxes || 1));
  const mailboxLimitReached = allConnectedMailboxes.length >= mailboxLimit;
  const canResearchAndDraft = Boolean(planLimits.researchAndDraft);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('nudge.lastActivationScan');
      if (!raw) return;
      sessionStorage.removeItem('nudge.lastActivationScan');
      setActivationScanResult(JSON.parse(raw));
    } catch {
      sessionStorage.removeItem('nudge.lastActivationScan');
    }
  }, []);

  useEffect(() => {
    const clearers = [
      [activationScanResult, setActivationScanResult],
      [scanResult, setScanResult],
      [syncResult, setSyncResult],
      [draftResult, setDraftResult],
    ];
    const timers = clearers
      .filter(([value]) => Boolean(value))
      .map(([, setter]) => setTimeout(() => setter(null), 7000));
    return () => timers.forEach(clearTimeout);
  }, [activationScanResult, scanResult, syncResult, draftResult]);

  function handleConnectAnotherMailbox() {
    if (!canManageOrganization) return;
    if (mailboxLimitReached) {
      setUpgradeOpen(true);
      return;
    }

    navigate('/setup');
  }


  async function loadOpportunities({ silent = false } = {}) {
    if (!silent) setOpportunitiesLoading(true);
    setOpportunityError('');
    try {
      const result = await listNudgeOpportunities({ limit: 120, workspaceId: activeWorkspaceId });
      setOpportunities(result?.opportunities || result?.items || []);
      return result;
    } catch (err) {
      setOpportunityError(err?.message || 'Could not load opportunities.');
      return null;
    } finally {
      if (!silent) setOpportunitiesLoading(false);
    }
  }

  useEffect(() => {
    if (state.user) loadOpportunities({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.id, state.active, activeWorkspaceId, a.watching, a.pendingDrafts, a.awaitingReply, a.draftDeleted]);

  async function refreshHomeInBackground({ syncProvider = false } = {}) {
    if (!state.user || backgroundRefreshInFlightRef.current) return;

    backgroundRefreshInFlightRef.current = true;
    try {
      const now = Date.now();
      const shouldSyncProvider = (
        syncProvider &&
        state.active &&
        hasSupportedMailbox &&
        now - Number(lastProviderSyncAtRef.current || 0) >= 15000
      );

      if (shouldSyncProvider) {
        lastProviderSyncAtRef.current = now;
        await syncWatchedThreads({
          maxThreads: 50,
          workspaceId: activeWorkspaceId,
        }).catch(() => null);
      }

      await refreshAppState().catch(() => null);
      await loadOpportunities({ silent: true });
    } finally {
      backgroundRefreshInFlightRef.current = false;
    }
  }

  useEffect(() => {
    if (!state.user) return undefined;

    const poll = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshHomeInBackground({ syncProvider: false });
      }
    }, 30000);

    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      refreshHomeInBackground({ syncProvider: true });
    };

    window.addEventListener('focus', refreshOnReturn);
    document.addEventListener('visibilitychange', refreshOnReturn);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener('focus', refreshOnReturn);
      document.removeEventListener('visibilitychange', refreshOnReturn);
    };
    // Deliberately keyed to identity/workspace rather than every analytics change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user?.id, state.active, activeWorkspaceId, hasSupportedMailbox]);

  async function handleOpportunityRefresh() {
    setOpportunitiesLoading(true);
    setOpportunityError('');
    try {
      if (hasSupportedMailbox && state.active) {
        await syncWatchedThreads({ maxThreads: 50, workspaceId: activeWorkspaceId }).catch(() => null);
        await refreshAppState();
      }
      await loadOpportunities({ silent: true });
    } finally {
      setOpportunitiesLoading(false);
    }
  }

  async function handleOpportunityDraft(item) {
    const id = item.threadId || item.id;
    setOpportunityActionId(id);
    setDraftError('');
    setDraftResult(null);
    try {
      const result = await draftNextFollowUp({ threadId: id, force: true });
      setDraftResult(result);
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setDraftError(err?.message || 'Could not create a draft follow-up.');
    } finally {
      setOpportunityActionId('');
    }
  }

  async function handleOpportunityBulkDraft(selectedItems = []) {
    const uniqueItems = [];
    const seen = new Set();
    for (const item of selectedItems || []) {
      const id = item?.threadId || item?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      uniqueItems.push(item);
    }
    if (!uniqueItems.length) return;

    setOpportunityActionId('__bulk__');
    setDraftError('');
    setDraftResult(null);

    try {
      let created = 0;
      let failed = 0;
      for (const item of uniqueItems) {
        const id = item.threadId || item.id;
        try {
          await draftNextFollowUp({ threadId: id, force: true });
          created += 1;
        } catch {
          failed += 1;
        }
      }

      if (created) {
        setDraftResult({ ok: failed === 0, drafted: created, failed, message: failed ? `${created} draft${created === 1 ? '' : 's'} created, ${failed} failed.` : `${created} draft${created === 1 ? '' : 's'} created.` });
      } else {
        setDraftError('Could not create drafts for the selected opportunities.');
      }

      await refreshAppState();
      await loadOpportunities({ silent: true });
    } finally {
      setOpportunityActionId('');
    }
  }

  async function openResearchDrawer(item, prefetched = null) {
    const id = item?.threadId || item?.id;
    if (!id) return;
    setResearchDrawerItem(item);
    setResearchDrawerOpen(true);
    setResearchDrawerError('');
    if (prefetched?.runId || prefetched?.providerResults?.length) {
      setResearchDrawerLoading(false);
      setResearchDrawerData(prefetched);
      return;
    }
    setResearchDrawerData(null);
    setResearchDrawerLoading(true);
    try {
      const result = await getResearchRun({ threadId: id, runId: item?.lastResearch?.runId || '' });
      setResearchDrawerData(result?.research || null);
    } catch (err) {
      setResearchDrawerError(err?.message || 'Could not load the saved research for this opportunity.');
    } finally {
      setResearchDrawerLoading(false);
    }
  }

  async function handleOpportunityResearchDraft(item) {
    const id = item?.threadId || item?.id;
    if (!id) return;
    setResearchActionId(id);
    setDraftError('');
    setDraftResult(null);
    try {
      const result = await researchAndDraft({ threadId: id, force: true });
      setDraftResult({ ...result, message: result?.message || 'Research complete. A contextual follow-up draft was created in your mailbox.' });
      await openResearchDrawer(item, result?.research || null);
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setDraftError(err?.message || 'Could not research this contact and create a draft.');
    } finally {
      setResearchActionId('');
    }
  }

  async function handleOpportunityBulkResearchDraft(selectedItems = []) {
    const uniqueItems = [];
    const seen = new Set();
    for (const item of selectedItems || []) {
      const id = item?.threadId || item?.id;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      uniqueItems.push(item);
    }
    if (!uniqueItems.length) return;

    setResearchActionId('__bulk__');
    setDraftError('');
    setDraftResult(null);
    try {
      let created = 0;
      let failed = 0;
      let lastResult = null;
      for (const item of uniqueItems) {
        try {
          lastResult = await researchAndDraft({ threadId: item.threadId || item.id, force: true });
          created += 1;
        } catch {
          failed += 1;
        }
      }
      if (created) {
        setDraftResult({
          ...(lastResult || {}),
          ok: failed === 0,
          drafted: created,
          failed,
          message: failed
            ? `Research & Draft created ${created} draft${created === 1 ? '' : 's'}; ${failed} failed.`
            : `Research & Draft created ${created} contextual draft${created === 1 ? '' : 's'}.`,
        });
      } else {
        setDraftError('Research & Draft could not create drafts for the selected opportunities.');
      }
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } finally {
      setResearchActionId('');
    }
  }

  async function handleOpportunityFeedback(item, action) {
    const id = item.threadId || item.id;
    setOpportunityActionId(id);
    setOpportunityError('');
    try {
      await updateNudgeOpportunity({ threadId: id, action });
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setOpportunityError(err?.message || 'Could not update this opportunity.');
    } finally {
      setOpportunityActionId('');
    }
  }

  async function handleOpportunityAutoSendApproval(item, approved = true) {
    const id = item?.threadId || item?.id;
    if (!id) return;

    setOpportunityActionId(id);
    setOpportunityError('');

    try {
      await updateNudgeOpportunity({
        threadId: id,
        action: approved ? 'approve_auto_send' : 'revoke_auto_send',
      });
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setOpportunityError(err?.message || 'Could not update auto-send approval.');
    } finally {
      setOpportunityActionId('');
    }
  }


  async function handleReconnectMailbox(mailbox) {
    const provider = mailbox?.provider || '';
    const id = mailbox?.mailboxId || mailbox?.id || `${provider}-${mailbox?.email || ''}`;
    if (!provider) return;

    if (['imap', 'yahoo', 'icloud'].includes(provider)) {
      navigate('/setup');
      return;
    }

    setMailboxActionId(id);
    setScanError('');
    try {
      const result = await connectMailbox(provider, state.user?.email, { workspaceId: activeWorkspaceId, loginHint: mailbox?.email || '' });
      if (!result?.redirecting) {
        await refreshAppState();
        setScanResult({ ok: true, message: `${providerDisplayName(provider)} reconnected.` });
      }
    } catch (err) {
      setScanError(err?.message || 'Could not reconnect mailbox.');
    } finally {
      setMailboxActionId('');
    }
  }

  async function handleDisconnectMailbox(mailbox) {
    const id = mailbox?.mailboxId || mailbox?.id || '';
    const email = mailbox?.email || '';
    const provider = mailbox?.provider || '';
    const confirmed = window.confirm(`Disconnect ${email || 'this mailbox'} from Nudge? Nudge will stop scanning it and hide open opportunities tied to this mailbox.`);
    if (!confirmed) return;

    setMailboxActionId(id || `${provider}-${email}`);
    setScanError('');
    try {
      await disconnectNudgeMailbox({ mailboxId: id, id, provider, email });
      await refreshAppState();
      await loadOpportunities({ silent: true });
      setScanResult({ ok: true, message: `${email || 'Mailbox'} disconnected from Nudge.` });
    } catch (err) {
      setScanError(err?.message || 'Could not disconnect mailbox.');
    } finally {
      setMailboxActionId('');
    }
  }

  async function openAddConversation() {
    if (!primaryMailbox) return;

    setAddConversationOpen(true);
    setSentConversations([]);
    setSentConversationsError('');
    setSentConversationSearch('');
    setSentConversationsLoading(true);

    try {
      const result = await callNudge('nudgeListSentConversations', {
        mailboxId: primaryMailbox.mailboxId || primaryMailbox.id,
        limit: 30,
        organizationId: state.organization?.id || '',
      });
      setSentConversations(result.items || []);
    } catch (err) {
      setSentConversationsError(err?.message || 'Could not load recent sent conversations.');
    } finally {
      setSentConversationsLoading(false);
    }
  }

  async function handleAddConversation(item) {
    const providerThreadId = String(item?.providerThreadId || '');
    if (!primaryMailbox || !providerThreadId || item?.alreadyAdded || item?.canAdd === false) return;

    setAddingConversationId(providerThreadId);
    setSentConversationsError('');

    try {
      const result = await callNudge('nudgeAddConversation', {
        mailboxId: primaryMailbox.mailboxId || primaryMailbox.id,
        providerThreadId,
        organizationId: state.organization?.id || '',
      });

      setSentConversations((current) => current.map((conversation) =>
        conversation.providerThreadId === providerThreadId
          ? {
              ...conversation,
              alreadyAdded: true,
              canAdd: false,
              unavailableReason: 'Already in Nudge',
            }
          : conversation
      ));

      setScanResult({
        ok: true,
        found: 1,
        message: result.message || 'Conversation added to Nudge.',
      });

      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setSentConversationsError(err?.message || 'Could not add this conversation.');
    } finally {
      setAddingConversationId('');
    }
  }

  async function handleScan() {
    if (!primaryMailbox) return;
    setScanError('');
    setScanResult(null);
    setScanning(true);
    try {
      const result = await scanNudgeMailbox({ mailboxId: primaryMailbox.mailboxId || primaryMailbox.id, maxThreads: 50 });
      setScanResult(result);
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setScanError(err?.message || 'Could not scan this mailbox.');
    } finally {
      setScanning(false);
    }
  }

  async function handleDraft() {
    setDraftError('');
    setDraftResult(null);
    setDrafting(true);
    try {
      const result = await draftNextFollowUp({ force: true });
      setDraftResult(result);
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setDraftError(err?.message || 'Could not create a draft follow-up.');
    } finally {
      setDrafting(false);
    }
  }

  async function handleSync() {
    setSyncError('');
    setSyncResult(null);
    setSyncing(true);
    try {
      const result = await syncWatchedThreads({ maxThreads: 50, workspaceId: activeWorkspaceId });
      setSyncResult(result);
      await refreshAppState();
      await loadOpportunities({ silent: true });
    } catch (err) {
      setSyncError(err?.message || 'Could not sync mailbox status.');
    } finally {
      setSyncing(false);
    }
  }


  async function handleStatusChange() {
    if (!hasActiveConnectedMailbox && !state.active) {
      navigate('/setup');
      return;
    }
    const next = !state.active;
    setStatusError('');
    setChangingStatus(true);
    try {
      await setNudgeStatus(next);
      update({ active: next });
      await refreshAppState();
    } catch (err) {
      setStatusError(err?.message || 'Could not change Nudge status.');
    } finally {
      setChangingStatus(false);
    }
  }

  return (
    <Box className="app-grid" minHeight="100vh">
      <AppHeader />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4.5 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2.5} alignItems={{ md: 'center' }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: state.active ? 'success.main' : 'text.disabled' }} />
              <Typography variant="body2" fontWeight={850} color={state.active ? 'success.dark' : 'text.secondary'}>{state.active ? 'Nudge active' : 'Nudge paused'}</Typography>
              <Chip size="small" variant="outlined" label={planName} />
            </Stack>
            <Typography variant="h1" sx={{ mt: .8, fontSize: { xs: 34, md: 44 } }}>Follow-up overview</Typography>
            <Typography color="text.secondary" sx={{ mt: .6, fontSize: 16 }}>{setupSummary}</Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" startIcon={<SearchRoundedIcon />} disabled={scanning || !hasActiveConnectedMailbox} onClick={handleScan}>{scanning ? 'Scanning…' : 'Find conversations'}</Button>
            <Button variant="outlined" startIcon={<SyncRoundedIcon />} disabled={syncing || !hasActiveConnectedMailbox || a.watching < 1} onClick={handleSync}>{syncing ? 'Syncing…' : 'Sync'}</Button>
            {canManageOrganization && <Button variant={state.active ? 'outlined' : 'contained'} color={state.active ? 'inherit' : 'primary'} startIcon={state.active ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />} disabled={changingStatus} onClick={handleStatusChange}>{changingStatus ? 'Saving…' : state.active ? 'Pause' : hasActiveConnectedMailbox ? 'Activate' : 'Connect mailbox'}</Button>}
          </Stack>
        </Stack>

        {statusError && <Alert severity="error" sx={{ mt: 2.5 }}>{statusError}</Alert>}
        {scanError && <Alert severity="error" sx={{ mt: 2.5 }}>{scanError}</Alert>}
        {draftError && <Alert severity="error" sx={{ mt: 2.5 }}>{draftError}</Alert>}
        {syncError && <Alert severity="error" sx={{ mt: 2.5 }}>{syncError}</Alert>}
        {creditsExhausted && <Alert severity="warning" sx={{ mt: 2.5 }}>You are out of follow-up credits. Discovery and reply monitoring can continue, but new included-AI drafts are paused.</Alert>}
        {activationScanResult && <Alert severity={activationScanResult.found > 0 ? 'success' : 'info'} sx={{ mt: 2.5 }}>Setup complete. {activationScanResult.message || `Nudge found ${activationScanResult.found || 0} conversations to watch.`}</Alert>}
        {scanResult && <Alert severity={scanResult.found > 0 ? 'success' : 'info'} sx={{ mt: 2.5 }}>{scanResult.message || `Nudge found ${scanResult.found || 0} conversations to watch.`}</Alert>}
        {syncResult && <Alert severity={syncResult.changed > 0 ? 'success' : 'info'} sx={{ mt: 2.5 }}>{syncResult.message || `Synced ${syncResult.checked || 0} conversations.`}</Alert>}
        {draftResult && (
          <Alert severity={draftResult.ok ? 'success' : 'info'} sx={{ mt: 2.5 }} action={draftResult.ok ? (
            <Stack direction="row" spacing={1}>
              <Button color="inherit" size="small" component="a" href={draftResult.providerInboxUrl || draftResult.gmailInboxUrl || mailboxUrls.inbox || '#'} target="_blank" rel="noreferrer">Inbox</Button>
              <Button color="inherit" size="small" component="a" href={draftResult.providerDraftsUrl || draftResult.gmailDraftsUrl || mailboxUrls.drafts || '#'} target="_blank" rel="noreferrer">Drafts</Button>
            </Stack>
          ) : null}>{draftResult.message || 'Draft request completed.'}</Alert>
        )}

        <Box sx={{ mt: 3, bgcolor: '#101228', color: '#eef0ff', borderRadius: 2.3, overflow: 'hidden' }}>
          <Grid container>
            <Grid item xs={12} md={4.5} sx={{ px: 2.6, py: 2.2, borderRight: { md: '1px solid rgba(255,255,255,.09)' } }}>
              <Typography variant="caption" sx={{ color: '#aeb3d4', textTransform: 'uppercase', letterSpacing: '.07em', fontWeight: 850 }}>Current workflow</Typography>
              <Typography sx={{ mt: .35, fontSize: 20, fontWeight: 900 }}>{state.settings.mode === 'draft' ? 'Draft mode' : 'Auto-send mode'} · {state.settings.cadence}</Typography>
              <Stack direction="row" spacing={.8} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                {showNextFollowUp && <Chip size="small" label={`Next due ${nextFollowUp}`} sx={{ bgcolor: 'rgba(255,182,72,.16)', color: '#ffd79d' }} />}
                {state.settings.mode === 'send' && (
                  <Chip
                    size="small"
                    label={(state.settings.autoSendPolicy || 'all') === 'approval'
                      ? 'Approval required'
                      : (state.settings.autoSendPolicy || 'all') === 'allowlist'
                        ? 'Allow-list only'
                        : 'Broad auto-send'}
                    sx={{ bgcolor: 'rgba(255,255,255,.07)', color: '#ccd0e8' }}
                  />
                )}
                {a.lastScan?.scannedAt && <Chip size="small" label={`Last scan: ${a.lastScan.found || 0} found`} sx={{ bgcolor: 'rgba(255,255,255,.07)', color: '#ccd0e8' }} />}
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3} md={1.7} sx={{ px: 2, py: 2.2, borderRight: '1px solid rgba(255,255,255,.09)' }}><Typography variant="caption" sx={{ color: '#aeb3d4' }}>Active</Typography><Typography sx={{ fontSize: 26, fontWeight: 900 }}>{formatNumber(a.watching)}</Typography></Grid>
            <Grid item xs={6} sm={3} md={1.7} sx={{ px: 2, py: 2.2, borderRight: { sm: '1px solid rgba(255,255,255,.09)' } }}><Typography variant="caption" sx={{ color: '#aeb3d4' }}>Drafted</Typography><Typography sx={{ fontSize: 26, fontWeight: 900 }}>{formatNumber(a.drafted)}</Typography></Grid>
            <Grid item xs={6} sm={3} md={1.7} sx={{ px: 2, py: 2.2, borderRight: '1px solid rgba(255,255,255,.09)' }}><Typography variant="caption" sx={{ color: '#aeb3d4' }}>Sent</Typography><Typography sx={{ fontSize: 26, fontWeight: 900 }}>{formatNumber(a.sent)}</Typography></Grid>
            <Grid item xs={6} sm={3} md={2.4} sx={{ px: 2, py: 2.2 }}>
              <Stack direction="row" justifyContent="space-between" gap={1}><Typography variant="caption" sx={{ color: '#aeb3d4' }}>Credits</Typography><Typography variant="caption" sx={{ color: '#ffd79d', fontWeight: 800 }}>{creditsRemaining} left</Typography></Stack>
              <LinearProgress variant="determinate" value={creditPercent} sx={{ mt: .8, height: 6, borderRadius: 99, bgcolor: 'rgba(255,255,255,.1)', '& .MuiLinearProgress-bar': { bgcolor: '#FFB648', borderRadius: 99 } }} />
              <Button size="small" onClick={() => navigate('/settings?section=credits')} sx={{ mt: .45, p: 0, minWidth: 0, color: '#ffd79d' }}>Manage</Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ position: 'relative', mt: 3 }}>
          <Box sx={{ opacity: hasActiveConnectedMailbox ? 1 : .18, filter: hasActiveConnectedMailbox ? 'none' : 'grayscale(.2) blur(.4px)', pointerEvents: hasActiveConnectedMailbox ? 'auto' : 'none' }}>
            <OpportunityReviewTable
              items={opportunities}
              loading={opportunitiesLoading}
              error={opportunityError}
              actionId={opportunityActionId}
              researchActionId={researchActionId}
              onRefresh={handleOpportunityRefresh}
              onAddConversation={openAddConversation}
              onDraft={handleOpportunityDraft}
              onBulkDraft={handleOpportunityBulkDraft}
              onResearchDraft={handleOpportunityResearchDraft}
              onBulkResearchDraft={handleOpportunityBulkResearchDraft}
              onViewResearch={(item) => openResearchDrawer(item)}
              onFeedback={handleOpportunityFeedback}
              onAutoSendApproval={handleOpportunityAutoSendApproval}
              onOpenSafety={() => navigate('/settings?section=safety')}
              researchEnabled={canResearchAndDraft}
              autoCreateDrafts={Boolean(state.settings?.autoCreateDrafts)}
              onConnectResearch={() => navigate('/settings?section=enrichment')}
              creditsExhausted={creditsExhausted}
            />
          </Box>
          {!hasActiveConnectedMailbox && (
            <Box sx={{ position: 'absolute', inset: 0, minHeight: 260, display: 'grid', placeItems: 'center', p: 2, pointerEvents: 'none' }}>
              <Box sx={{ maxWidth: 460, bgcolor: '#fff', border: '1px solid rgba(26,29,49,.1)', borderRadius: 2, p: 3, pointerEvents: 'auto', textAlign: 'center', boxShadow: '0 18px 50px rgba(26,29,49,.12)' }}>
                <Typography variant="h3" sx={{ fontSize: 23 }}>Connect a mailbox to see missed opportunities.</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>Scanning and drafts resume as soon as this workspace has an active mailbox.</Typography>
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate('/setup')} sx={{ mt: 2 }}>Connect mailbox</Button>
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2.5, px: 2.2, py: 1.8, bgcolor: 'rgba(255,255,255,.72)', border: '1px solid rgba(26,29,49,.09)', borderRadius: 1.8 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1.5} alignItems={{ md: 'center' }}>
            <Box><Typography fontWeight={900}>Quick actions</Typography><Typography variant="body2" color="text.secondary">Use these for manual control; normal automation continues in the background.</Typography></Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="contained" startIcon={<DraftsOutlinedIcon />} disabled={drafting || a.watching < 1 || !hasActiveConnectedMailbox || creditsExhausted} onClick={handleDraft}>{drafting ? 'Creating…' : 'Draft next'}</Button>
              {mailboxUrls.inbox && <ExternalButton href={mailboxUrls.inbox} startIcon={<InboxOutlinedIcon />} sx={{ minWidth: 100 }}>Inbox</ExternalButton>}
              {mailboxUrls.drafts && <ExternalButton href={mailboxUrls.drafts} startIcon={<DraftsOutlinedIcon />} sx={{ minWidth: 100 }}>Drafts</ExternalButton>}
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} lg={7}>
            <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(26,29,49,.09)', borderRadius: 2.2, overflow: 'hidden', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(26,29,49,.08)' }}>
                <Box><Typography variant="h3" sx={{ fontSize: 21 }}>Recent activity</Typography><Typography variant="body2" color="text.secondary">Drafts, sends, replies, and lifecycle changes.</Typography></Box>
                {a.draftDeleted > 0 && <Chip size="small" color="warning" label={`${a.draftDeleted} deleted`} />}
              </Stack>
              <Box sx={{ px: 2.5, py: 1 }}>
                {a.recentActivity?.length ? a.recentActivity.slice(0, 8).map((activity) => <ActivityRow key={activity.id || `${activity.type}-${activity.createdAt}`} activity={activity} />) : <Typography color="text.secondary" variant="body2" sx={{ py: 3 }}>No lifecycle activity yet.</Typography>}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Box sx={{ bgcolor: '#fff', border: '1px solid rgba(26,29,49,.09)', borderRadius: 2.2, overflow: 'hidden', height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(26,29,49,.08)' }}>
                <Box><Typography variant="h3" sx={{ fontSize: 21 }}>Mailboxes</Typography><Typography variant="body2" color="text.secondary">{activeWorkspace?.name || 'Current workspace'}</Typography></Box>
                {canManageOrganization && <Button size="small" startIcon={<AddRoundedIcon />} onClick={handleConnectAnotherMailbox}>{mailboxLimitReached ? 'Add mailbox' : 'Connect'}</Button>}
              </Stack>
              <Box>
                {connectedMailboxes.length ? connectedMailboxes.map((mailbox) => {
                  const mailboxId = mailbox.mailboxId || mailbox.id || `${mailbox.provider}-${mailbox.email}`;
                  const needsReconnect = mailboxNeedsReconnect(mailbox);
                  const busy = mailboxActionId === mailboxId;
                  return (
                    <Stack key={mailboxId} direction="row" alignItems="center" spacing={1.4} sx={{ px: 2.5, py: 1.7, borderBottom: '1px solid rgba(26,29,49,.07)', '&:last-of-type': { borderBottom: 0 } }}>
                      <ProviderLogo provider={mailbox.provider} size={36} />
                      <Box sx={{ minWidth: 0, flex: 1 }}><Typography fontWeight={800} noWrap>{mailbox.email}</Typography><Typography variant="caption" color={needsReconnect ? 'warning.dark' : 'text.secondary'}>{needsReconnect ? 'Needs reconnect' : mailboxStatusCopy({ ...mailbox, mode: state.settings.mode }, state.active)}</Typography></Box>
                      {needsReconnect ? (canManageOrganization ? <Button size="small" variant="contained" startIcon={<ReplayRoundedIcon />} onClick={() => handleReconnectMailbox(mailbox)} disabled={Boolean(mailboxActionId)}>{busy ? '…' : 'Reconnect'}</Button> : <Chip size="small" color="warning" label="Admin action" />) : <Button size="small" color="inherit" onClick={() => navigate('/settings?section=mailboxes')}>Manage</Button>}
                    </Stack>
                  );
                }) : <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>No mailbox in this workspace.</Typography>}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Dialog
        open={addConversationOpen}
        onClose={() => !addingConversationId && setAddConversationOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: .8 }}>Add conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a sent conversation you definitely want Nudge to follow. Adding it skips opportunity discovery, but the follow-up still uses your normal cadence and safety rules.
          </Typography>

          <TextField
            fullWidth
            size="small"
            value={sentConversationSearch}
            onChange={(event) => setSentConversationSearch(event.target.value)}
            placeholder="Search subject or recipient"
            sx={{ mb: 1.5 }}
          />

          {sentConversationsError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{sentConversationsError}</Alert>
          )}

          {sentConversationsLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={24} />
            </Stack>
          ) : visibleSentConversations.length ? (
            <Stack spacing={0}>
              {visibleSentConversations.map((item) => {
                const busy = addingConversationId === item.providerThreadId;
                const disabled = Boolean(addingConversationId) || item.alreadyAdded || item.canAdd === false;

                return (
                  <Stack
                    key={item.providerThreadId}
                    direction={{ xs: 'column', sm: 'row' }}
                    gap={1.2}
                    alignItems={{ sm: 'center' }}
                    sx={{
                      py: 1.35,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-of-type': { borderBottom: 0 },
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography fontWeight={800} noWrap>{item.subject || '(no subject)'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {(item.recipientEmails || []).join(', ') || 'Unknown recipient'}
                        {item.lastOutboundAt ? ` · ${formatWhen(item.lastOutboundAt)}` : ''}
                      </Typography>
                      {item.unavailableReason && (
                        <Typography variant="caption" color={item.alreadyAdded ? 'text.secondary' : 'warning.dark'} sx={{ display: 'block', mt: .25 }}>
                          {item.unavailableReason}
                        </Typography>
                      )}
                    </Box>

                    <Button
                      size="small"
                      variant={item.alreadyAdded ? 'text' : 'outlined'}
                      disabled={disabled}
                      onClick={() => handleAddConversation(item)}
                      sx={{ minWidth: 72, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                    >
                      {busy ? 'Adding…' : item.alreadyAdded ? 'Added' : 'Add'}
                    </Button>
                  </Stack>
                );
              })}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No recent sent conversations found in your selected lookback period.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddConversationOpen(false)} disabled={Boolean(addingConversationId)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ResearchDrawer
        open={researchDrawerOpen}
        onClose={() => setResearchDrawerOpen(false)}
        item={researchDrawerItem}
        research={researchDrawerData}
        loading={researchDrawerLoading}
        error={researchDrawerError}
      />
      <UpgradeModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        reason={mailboxLimitReached ? `Your current plan includes ${mailboxLimit} mailbox${mailboxLimit === 1 ? '' : 'es'}.` : 'Upgrade for more mailboxes and automation capacity.'}
        subscription={state.subscription}
        launchRef={state.pendingReferralCode}
        onUpgraded={refreshAppState}
      />
    </Box>
  );


}
